// frontend/src/components/chat/patient-panel/hooks/useProblemRanking.ts
import { useState, useCallback, useMemo } from 'react'
import { SemanticExtraction } from '../../../../contexts/ChatContext'
import { EnhancedProblem } from '../types'

export const useProblemRanking = (
  extractions: SemanticExtraction[],
  _patientId: string // unused after localStorage removal; retained to match call site
) => {
  // Session-only state for pinned items (no localStorage)
  const [pinnedIds, setPinnedIds] = useState<string[]>([])

  // Get problems from extractions, sorted by priority
  const problems = useMemo(() => {
    if (!Array.isArray(extractions)) return []
    return extractions
      .filter(e => e.category === 'problem')
      .map((e, index): EnhancedProblem => ({
        id: `problem-${index}`,
        extraction: e,
        priority: e.priority ?? 5,
        status: e.status ?? 'unknown',
        isPinned: pinnedIds.includes(`problem-${index}`),
      }))
      .sort((a, b) => {
        // Pinned first
        if (a.isPinned && !b.isPinned) return -1
        if (!a.isPinned && b.isPinned) return 1
        // Then by priority (1 = highest)
        return a.priority - b.priority
      })
  }, [extractions, pinnedIds])

  // Reorder is a no-op now (priority-based only)
  // Could re-enable drag-and-drop later if needed
  const handleReorder = useCallback((_newOrder: EnhancedProblem[]) => {
    // No-op: always sort by priority
  }, [])

  const togglePin = useCallback((problemId: string) => {
    setPinnedIds(prev =>
      prev.includes(problemId)
        ? prev.filter(id => id !== problemId)
        : [...prev, problemId]
    )
  }, [])

  return {
    problems,
    handleReorder,
    togglePin,
  }
}
